import React from 'react';

import { Footer } from '../components/footer';
import { Camp67Header } from '../components/camp67-header/camp67-header';

import { Divider } from '../components/divider';
import { Link } from '@remix-run/react';
import { Page } from '../pages/pages-types';
import clsx from 'clsx';
import { pageStore } from '../pages/page-store';
import { FormattedString, useMessages } from '../i18n/useMessages';

export const ContentPage: React.FC<Page> = ({
  heroImage,
  heroImageAlt,
  title: titleKey,
  readingTime,
  summary,
  content,
  linkedPages,
  maxWidth = false,
}) => {
  const getMessage = useMessages();

  const allLinkedPages = linkedPages?.(pageStore) ?? [];
  const title = getMessage(titleKey);

  return (
    <>
      <section className="relative flex flex-col w-dvw min-h-[500px] sm:min-h-[800px] h-[80dvh] max-h-[1080px]">
        <Camp67Header isLogoLight={true} isDarkGlowHidden={false} isSunHidden={true} />
        <img
          className="h-full w-full object-cover -z-10 absolute top-0 left-0 object-[50%_70%]"
          alt={heroImageAlt}
          src={heroImage}
        />
        <div className="bg-content-top-wave w-full h-60 mt-auto -mb-10 bg-cover" />
      </section>
      {/** Taken from existing footer.tsx, line:99 and then modified. Need to update footer. */}
      <main className=" bg-yellow-50 pb-20">
        <div className="text-left bg-yellow-50 w-full px-12 sm:px-32 md:px-44 lg:px-72 -mt-20 relative">
          <h1 className="text-2xl leading-6 md:leading-8 md:text-5xl font-serif font-bold">
            {title}
          </h1>
          <p className="uppercase font-sans tracking-widest text-gray-500 text-sm md:mt-2">
            {readingTime}{' '}
            <FormattedString message="camp67-content-page.child.reading-time" />
          </p>
          <p className="font-serif text-xl mt-9 max-w-110 text-gray-500">{summary}</p>
          <Divider
            className={clsx(
              'border-dashed border-gray-500 my-10',
              maxWidth ? 'w-full' : 'max-w-[640px]',
            )}
          />
          <div
            className={clsx(
              'text-lg leading-relaxed',
              maxWidth ? 'w-full' : 'max-w-[640px]',
            )}
          >
            {content}
          </div>
        </div>
        {allLinkedPages && allLinkedPages.length > 0 ? (
          <section className="text-left w-full px-12 sm:px-32 md:px-44 lg:px-72 relative mt-12">
            <h2 className="uppercase font-serif tracking-wider text-black text-xl">
              <FormattedString message="camp67-content-page.read-more" />
            </h2>
            <div className="grid grid-cols-none grid-rows-1 md:grid-cols-2 md:grid-rows-none lg:grid-cols-3 gap-4 mt-6">
              {allLinkedPages.map(({ path, readingTime, subtitle, title }, index) => (
                <article key={index}>
                  <Link prefetch="intent" to={path}>
                    <button className="px-4 py-8 bg-white rounded-lg w-full h-full shadow-md-blur shadow-black/20">
                      <p className="font-serif text-blue-200 text-lg">
                        <FormattedString message={title} /> &rarr;
                      </p>
                      <p className="mt-1">
                        <FormattedString message={subtitle} />
                      </p>
                      <p className="mt-5 text-xs text-gray-500 uppercase tracking-wider">
                        {readingTime}{' '}
                        <FormattedString message="camp67-content-page.child.reading-time" />
                      </p>
                    </button>
                  </Link>
                </article>
              ))}
            </div>
          </section>
        ) : null}
      </main>
      <Footer isHome={false} />
    </>
  );
};
